<template>
  <div class="container" ref="select">
    <label v-if="selfContent.label" :class="[{'required': selfContent.required, 'read-only': selfContent.readOnly }]" >{{ selfContent.label }}</label>

    <div @click="toggleSelect" class="wrapper-dropdown" :class="[{
          'required': selfContent.required,
          'active': isSelectExpanded,
          'bad_input': selfContent.error,
          'missing_input': isMissing,
          'read-only': selfContent.readOnly,
          'open-up': openUp
        }]">

      <div class="span-container" :class="{ 'original-label': initialLabel === selfContent.value?.label }">
        <span>{{ currentValue ? currentValue.label : "..." }}</span>
      </div>

      <ul v-if="isSelectExpanded" class="dropdown">
        <li class="search-container" @click.stop v-if="isSearchBarActivated">
          <input ref="searchInput" v-model="searchText" type="text" class="search-input" placeholder="Rechercher..."/>
        </li>
        <li v-for="(value, index) in filteredValues" :key="index" @click.stop="selectThis(value.value)">{{ value.label }}</li>
        <li v-if="filteredValues.length === 0"><br /></li>
      </ul>
    </div>
  </div>
</template>

<script>
 import { library } from '@fortawesome/fontawesome-svg-core'
 import { faLock } from "@fortawesome/free-solid-svg-icons";

 library.add(faLock);

export default {
  name: "CustomSelectField",
  props: ["content"],
  data() {
    return {
      isSearchBarActivated: false,
      selfContent: this.content,
      isSelectExpanded: false,
      initialLabel: undefined,
      openUp: false,
      searchText: ""
    };
  },
  computed: {
    // Champ obligatoire encore vide
    isMissing() {
      const value = this.selfContent.value;
      return !!this.selfContent.required && !this.selfContent.readOnly && (value === '' || value === undefined || value === null);
    },
    currentValue() {
      return this.selfContent.selectValues.find(
          (item) => item.value === this.selfContent.value
      );
    },
    filteredValues() {
      const normalize = (str) => str
          .normalize("NFD")                 // sépare les accents
          .replace(/[\u0300-\u036f]/g, "")  // supprime les accents
          .toLowerCase();

      const search = normalize(this.searchText);

      return this.selfContent.selectValues
        .filter(item => item.value !== this.selfContent.value)
        .filter(item => normalize(item.label).includes(search));
    }
  },
  watch: {
    "selfContent.value"(e) {
      this.selectThis(e);
    }
  },
  mounted() {
    this.initialLabel = this.selfContent.label;
    this.selectThis(this.selfContent.value);
  },
  methods: {
    toggleSelect() {
      if (!this.selfContent.readOnly) {
        this.isSelectExpanded = !this.isSelectExpanded;

        if (this.isSelectExpanded) {
          this.isSearchBarActivated = this.selfContent.selectValues && this.selfContent.selectValues.length >= 8;

          this.$nextTick(() => {
            const dropdown = this.$refs.select;
            if (dropdown) {
              const rect = dropdown.getBoundingClientRect();
              const spaceBelow = window.innerHeight - rect.bottom;
              const dropdownHeight = 240;
              this.openUp = spaceBelow < dropdownHeight;
            }
            if (this.$refs.searchInput) {
              this.$refs.searchInput.focus();
            }
          });

          const mouseUpHandler = (event) => {
            if (!this.isClickOnSelect(event)) {
              this.closeSelect();
              document.removeEventListener("mouseup", mouseUpHandler);
            }
          };

          document.addEventListener("mouseup", mouseUpHandler);
        } else {
          this.searchText = "";
        }
      }
    },
    closeSelect() {
      this.isSelectExpanded = false;
      this.searchText = "";
    },
    isClickOnSelect(event) {
      const clickedElement = event.target;
      const selectDiv = this.$refs.select;
      return selectDiv && selectDiv.contains(clickedElement);
    },
    selectThis(selectedOption) {
      if (selectedOption !== undefined) {
        this.selfContent.value = selectedOption;
        this.selfContent.error = false;
        this.closeSelect();
      }
    }
  }
};
</script>

<style lang="stylus" scoped>
  @require '~/src/common/style/common.styl';

  // Libellé : légende au-dessus du champ (dans le flux, plus de pastille sur la bordure)
  div label
    display block
    margin 8px 0 4px 2px
    font-weight 600
    font-size 0.82em
    color CTRL_LABEL
    &.read-only
      color CTRL_TEXT_MUTED
    &.required::after
      content "*"
      margin-left 4px
      font-weight bold
      color CTRL_ERROR

  .container
    position relative
    width 100%

  .wrapper-dropdown
    position relative

    display inline-block
    box-sizing border-box
    width 100%
    height CTRL_HEIGHT
    // la marge de droite laisse la place au chevron
    padding 0 40px 0 CTRL_PADDING_X

    border 1px solid CTRL_BORDER
    border-radius CTRL_RADIUS
    background-color CTRL_BG
    transition background-color CTRL_EASE, border-color CTRL_EASE, box-shadow CTRL_EASE

    text-align left
    line-height 1.2
    color CTRL_TEXT

    cursor pointer

    -webkit-user-select none
    -moz-user-select none
    -ms-user-select none
    user-select none

  .wrapper-dropdown.missing_input:not(.active)
    box-shadow 0 0 7px CTRL_MISSING_GLOW

  .wrapper-dropdown:not(.read-only):hover
    background-color CTRL_BG_HOVER
    border-color CTRL_BORDER_HOVER

  .wrapper-dropdown.active
    background-color CTRL_BG_FOCUS
    border-color CTRL_FOCUS
    box-shadow 0 0 0 3px CTRL_FOCUS_RING

  .span-container
    overflow hidden
    text-overflow ellipsis
    white-space nowrap
    width 100%
    height 100%
    display flex
    align-items center
    span
      overflow hidden
      text-overflow ellipsis
      white-space nowrap

  // Chevron (remplace le triangle blanc)
  .wrapper-dropdown::before
    content ''
    position absolute
    top 50%
    right 18px

    margin-top -7px
    width 7px
    height 7px

    border-style solid
    border-width 0 2px 2px 0
    border-color CTRL_TEXT_MUTED
    border-radius 1px
    transform rotate(45deg)
    transition transform CTRL_EASE, margin-top CTRL_EASE, border-color CTRL_EASE

  .wrapper-dropdown.active::before
    margin-top -1px
    border-color CTRL_FOCUS
    transform rotate(-135deg)

  // Liste déroulante : panneau blanc, bordure douce, ombre diffuse
  .dropdown
    position absolute
    z-index 2
    top calc(100% + 5px)
    right -1px
    left -1px
    box-sizing border-box
    margin 0
    padding 6px
    list-style none
    border 1px solid CTRL_BORDER
    border-radius CTRL_RADIUS_LG
    box-shadow 0 12px 28px -10px rgba(23, 35, 63, 0.22), 0 2px 8px rgba(23, 35, 63, 0.08)
    background-color white
    opacity 1
    visibility visible
    transform scaleY(1)
    transform-origin top
    overflow auto
    max-height 220px

  .search-container
    padding 10px

  .search-input
    box-sizing border-box
    width 100%
    margin 0
    height 32px
    padding 0 12px
    background-color CTRL_BG
    border 1px solid CTRL_BORDER
    border-radius CTRL_RADIUS_SM
    color CTRL_TEXT
    outline none
    font-size 14px
    transition background-color CTRL_EASE, border-color CTRL_EASE, box-shadow CTRL_EASE
    &::placeholder
      color CTRL_PLACEHOLDER
    &:focus
      background-color CTRL_BG_FOCUS
      border-color CTRL_FOCUS
      box-shadow 0 0 0 3px CTRL_FOCUS_RING

  .dropdown li
    padding 0 12px
    line-height 34px
    border-radius CTRL_RADIUS_SM
    overflow hidden
    text-overflow ellipsis
    white-space nowrap
    cursor pointer
    transition background-color CTRL_EASE

  .dropdown li:hover
    background-color rgba(THEME_COLOR_PRIMARY, 0.08)

  // La ligne de recherche n'est pas une option : pas de survol, l'anneau de focus ne doit pas être rogné
  .dropdown li.search-container
    padding 2px 0 6px
    line-height normal
    overflow visible
    cursor default
    &:hover
      background-color transparent

  .wrapper-dropdown.open-up .dropdown
    top auto
    bottom calc(100% + 5px)
    transform-origin bottom

  .bad_input
    border-color CTRL_ERROR !important
    background-color CTRL_ERROR_BG !important

  .wrapper-dropdown.bad_input.active
    box-shadow 0 0 0 3px CTRL_ERROR_RING

  .wrapper-dropdown.read-only
    background-color CTRL_DISABLED_BG
    border-color transparent
    color CTRL_TEXT_MUTED
    cursor auto
    &::before
      border-color CTRL_DISABLED_TEXT
</style>